import {Component, inject, OnInit, ViewChildren, QueryList} from '@angular/core';
import {TramRegistroService} from '../../../services/database/tramRegistro.service';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {IGetDashboardBalanceDependenciaTramites} from '../../../shared/interfaces/database/tramite';
import {CommonModule} from '@angular/common';
import {SkeletonModule} from 'primeng/skeleton';
import {Utils} from 'lib-base';
import {firstValueFrom, forkJoin} from 'rxjs';
import {ButtonModule} from 'primeng/button';
import {Router} from '@angular/router';
import {CustomTitleDirective} from '../../../shared/clases/custom-title.directive';
import {FilterDashboardComponent} from '../filter-dashboard/filter-dashboard.component';
import {FilterDatesEvent, IBasicData} from '../../../shared/interfaces/database/configuracion';
import {DropdownModule} from 'primeng/dropdown';
import {ChartDonutConfig, ChartDonutSlice, ChartDonutComponent} from '../components/chart-donut/chart-donut.component';

export interface DependenciaChartCard {
  dependenciaId: number;
  dependenciaNombre: string;
  config: ChartDonutConfig;
  slices: ChartDonutSlice[];
}

@Component({
  selector: 'app-tramite-por-dependencia',
  standalone: true,
  imports: [
    CommonModule,
    SkeletonModule,
    ButtonModule,
    FilterDashboardComponent,
    TranslatePipe,
    DropdownModule,
    ChartDonutComponent
  ],
  templateUrl: './tramite-por-dependencia.component.html',
  styleUrl: './tramite-por-dependencia.component.scss'
})
export class TramitePorDependenciaComponent implements OnInit {
  translations: any = {};
  isLoading: boolean = true;
  filterDependencias: IBasicData<number>[] = [];
  allCards: DependenciaChartCard[] = [];
  displayedCards: DependenciaChartCard[] = [];

  private titleService: CustomTitleDirective = inject(CustomTitleDirective);
  @ViewChildren('donutChart') donutCharts!: QueryList<ChartDonutComponent>;

  private readonly COLOR_PALETTE = ['#0099DD', '#2ccffd', '#1876c6', '#a855f7', '#ff4568', '#00e676'];

  constructor( private readonly _tr: TramRegistroService,
    private readonly _t: TranslateService,
    private readonly _r: Router) {  }

  async ngOnInit() {
    this.titleService.changeTitle('components.app-balance-general-tramite-por-dependencia.titulo1');
    await this.initTranslate();
    this.getDashboardBalanceDependenciaTramites();
  }

  private async initTranslate() {
    this.translations = await firstValueFrom(forkJoin({
      titulo1: this._t.get('components.app-balance-general-tramite-por-dependencia.titulo1'),
      verDetalle: this._t.get('msg.ver-detalle'),
    }));
  }

  //#region actionListeners

  changesDate(event: FilterDatesEvent) {
    this.getDashboardBalanceDependenciaTramites(event.startDate, event.endDate);
  }

  changeFilterDeps(itemValue: number) {
    if (itemValue == null) {
      this.clearFilterDependencias();
      return;
    }
    this.displayedCards = this.allCards.filter(c => c.dependenciaId === itemValue);
    this.loadChartsData();
  }

  clearFilterDependencias() {
    this.displayedCards = [...this.allCards];
    this.loadChartsData();
  }

  verDetalle(dependenciaId: number, dependenciaNombre: string) {
    const url = `/dashboard/detalle-tramite-dependencia/${dependenciaId}/${dependenciaNombre}`;
    this._r.navigate([url]).then();
  }

  //#endregion actionListeners

  //#region call api
  getDashboardBalanceDependenciaTramites(startDate?: Date, endDate?: Date) {
    this.isLoading = true;
    const start = startDate ? Utils.castDateToString(startDate) : undefined;
    const end = endDate ? Utils.castDateToString(endDate) : undefined;
    this._tr.getDashboardBalanceDependenciaTramites(start, end).subscribe({
      next: (data: IGetDashboardBalanceDependenciaTramites[]) => {
        this.setFilterDependencias(data);
        this.buildCards(data);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  //#endregion call api

  //#region utils
  private setFilterDependencias(data: IGetDashboardBalanceDependenciaTramites[]) {
    const uniqueMap = new Map<number, string>();
    data?.forEach(item => {
      if (!uniqueMap.has(item.dependenciaId)) {
        uniqueMap.set(item.dependenciaId, item.dependencia);
      }
    });
    this.filterDependencias = Array.from(uniqueMap.entries()).map(([value, descripcion]) => ({
      descripcion,
      value
    }));
  }

  private buildCards(data: IGetDashboardBalanceDependenciaTramites[]) {
    const grouped = (data || []).reduce((acc, curr) => {
      if (!acc[curr.dependenciaId]) {
        acc[curr.dependenciaId] = [];
      }
      acc[curr.dependenciaId].push(curr);
      return acc;
    }, {} as { [key: number]: IGetDashboardBalanceDependenciaTramites[] });

    this.allCards = this.createChartConfig(grouped);

    this.displayedCards = [...this.allCards];
    this.loadChartsData();
  }

  private createChartConfig(grouped: any): DependenciaChartCard[] {
    return Object.keys(grouped).map(depIdStr => {
      const depId = Number(depIdStr);
      const items: IGetDashboardBalanceDependenciaTramites[] = grouped[depId];
      const dependenciaNombre = items[0]?.dependencia || '';

      const completadoItem = items.find(i => i.estado.includes('Completados'));

      const totals:number = items?.map(it => it.value)?.reduce((acc: number, curr: number) => acc + curr, 0) ?? 1;
      const percentComplete:number = completadoItem ? (completadoItem.value / totals) * 100 : 0;
      const centerLabel = completadoItem ? `${percentComplete.toFixed(1)}%` : undefined;

      const slices: ChartDonutSlice[] = items.map((item:any, index:number) => ({
        label: item.estado,
        value: item.value,
        color: this.COLOR_PALETTE[index % this.COLOR_PALETTE.length]
      }));

      return {
        dependenciaId: depId,
        dependenciaNombre,
        config: {
          chartId: `donut-dep-${depId}`,
          centerLabel,
          slices: []
        },
        slices
      };
    });
  }

  private loadChartsData(): void {
    setTimeout(() => {
      this.donutCharts?.forEach((chartComp, index) => {
        const card = this.displayedCards[index];
        if (card) {
          chartComp.load(card.slices);
        }
      });
    }, 0);
  }

  //#endregion utils
}
